import { defineStore } from 'pinia'
import {
  fetchSessionApi,
  loginWithPasswordApi,
  loginWithTelegramApi,
  logoutApi,
  registerApi,
  registerCourierApi,
  registerRestaurantApi,
} from '@/entities/auth/api/authApi'
import { fetchPersonalProfileApi, updatePersonalProfileApi } from '@/entities/personal/api/personalApi'
import { bootstrapRealtime, teardownRealtime } from '@/app/bootstrapRealtime'
import { clearAppSessionState } from '@/app/clearAppSessionState'
import { useDeliveryAddressStore } from '@/entities/delivery-address/model/useDeliveryAddressStore'
import { useInviteRefStore } from '@/entities/referral/model/useInviteRefStore'
import { useUtmStore } from '@/entities/utm/model/useUtmStore'
import { useClientOrdersStore } from '@/features/client-orders/model/useClientOrdersStore'
import { useCartStore } from '@/features/cart/model/useCartStore'

function isUserRoleAllowedForApp(userRole, appRole) {
  if (!userRole) {
    return false
  }
  return userRole === appRole
}

async function syncAfterAuth(user) {
  if (APP_ROLE === 'client') {
    useDeliveryAddressStore().hydrateFromUser(user)
    try {
      await useCartStore().syncAfterAuth()
      await useClientOrdersStore().loadActiveOrder()
      await bootstrapRealtime()
    } catch (error) {
      console.warn('[client] sync after auth failed', error)
    }
  }

  if (APP_ROLE === 'restaurant' || APP_ROLE === 'courier') {
    try {
      await bootstrapRealtime()
    } catch (error) {
      console.warn('[realtime] bootstrap after auth failed', error)
    }
  }
}
import { mapAuthUser } from '@/entities/auth/model/mapAuth'
import { APP_ROLE } from '@/config/env'
import {
  adminHasAccessCode,
  adminHasAnyAccessCode,
} from '@/features/admin/lib/adminHasAccessCode'
import { resolveAdminFallbackRouteName } from '@/features/admin/lib/resolveAdminFallbackRoute'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loginError: null,
    isLoggingIn: false,
    roleMismatch: false,
    expectedAppRole: APP_ROLE,
  }),
  getters: {
    isLoggedIn: (state) => Boolean(state.user),
    hasRoleMismatch: (state) => state.roleMismatch,
    accessCodes: (state) => (state.user?.role === 'admin' ? state.user.accessCodes : null),
    hasAccessCode: (state) => (code) => adminHasAccessCode(state.user?.accessCodes ?? null, code),
    hasAnyAccessCode: (state) => (codes) =>
      adminHasAnyAccessCode(state.user?.accessCodes ?? null, codes),
    defaultAdminRouteName: (state) =>
      resolveAdminFallbackRouteName(state.user?.accessCodes ?? null),
  },
  actions: {
    setUser(dto) {
      this.user = dto ? mapAuthUser(dto) : null
      if (!this.user) {
        this.roleMismatch = false
      }
    },
    // Обновляет флаг привязки Telegram у текущего пользователя.
    setTelegramLinked(isLinked) {
      if (!this.user) {
        return
      }
      this.user = {
        ...this.user,
        sp_tg_linked: Boolean(isLinked),
      }
    },
    validateRoleForApp(appRole = APP_ROLE) {
      this.expectedAppRole = appRole
      if (!this.user) {
        this.roleMismatch = false
        return true
      }
      this.roleMismatch = !isUserRoleAllowedForApp(this.user.role, appRole)
      return !this.roleMismatch
    },
    async restoreSession() {
      try {
        const payload = await fetchSessionApi()
        this.setUser(payload.data)
        this.validateRoleForApp()
        if (APP_ROLE === 'client') {
          useDeliveryAddressStore().hydrateFromUser(this.user)
        }
      } catch (error) {
        const code = error?.code ?? error?.response?.data?.error
        if (error.response?.status === 401 || code === 'unauthorized') {
          this.setUser(null)
          teardownRealtime()
          clearAppSessionState()
          useDeliveryAddressStore().clear()
          return
        }
        console.warn('[auth] restoreSession failed', error)
      }
    },
    async refreshPersonalProfile() {
      if (!this.user?.id) {
        return null
      }
      const dto = await fetchPersonalProfileApi(this.user.id)
      if (dto) {
        this.setUser({ ...dto, type: dto.type ?? this.user.type })
        if (APP_ROLE === 'client') {
          useDeliveryAddressStore().hydrateFromUser(this.user)
        }
      }
      return this.user
    },
    async updatePersonalProfile(fields = {}) {
      const dto = await updatePersonalProfileApi(fields)
      if (dto) {
        this.setUser({
          ...dto,
          type: dto.type ?? this.user?.type,
          deliverable:
            typeof dto.deliverable === 'boolean' ? dto.deliverable : this.user?.deliverable,
        })
        if (APP_ROLE === 'client') {
          useDeliveryAddressStore().hydrateFromUser(this.user)
        }
      }
      return this.user
    },
    async loginWithPassword(email, password) {
      this.isLoggingIn = true
      this.loginError = null
      try {
        const payload = await loginWithPasswordApi({ email, password })
        this.setUser(payload.data)
        this.validateRoleForApp()
        await syncAfterAuth(this.user)
        return payload
      } catch (error) {
        this.loginError = error
        throw error
      } finally {
        this.isLoggingIn = false
      }
    },
    async register(form) {
      this.isLoggingIn = true
      this.loginError = null
      try {
        const inviteRef = useInviteRefStore().inviteRef
        const utm = useUtmStore().utm
        const payload = await registerApi({
          ...form,
          ref: form.ref ?? inviteRef ?? undefined,
          utm: utm ?? undefined,
        })
        this.setUser(payload.data)
        this.validateRoleForApp()
        await syncAfterAuth(this.user)
        return payload
      } catch (error) {
        this.loginError = error
        throw error
      } finally {
        this.isLoggingIn = false
      }
    },
    async registerCourier(form) {
      this.isLoggingIn = true
      this.loginError = null
      try {
        const payload = await registerCourierApi(form)
        this.setUser(payload.data)
        this.validateRoleForApp()
        await syncAfterAuth(this.user)
        return payload
      } catch (error) {
        this.loginError = error
        throw error
      } finally {
        this.isLoggingIn = false
      }
    },
    async registerRestaurant(form) {
      this.isLoggingIn = true
      this.loginError = null
      try {
        const payload = await registerRestaurantApi(form)
        this.setUser(payload.data)
        this.validateRoleForApp()
        await syncAfterAuth(this.user)
        return payload
      } catch (error) {
        this.loginError = error
        throw error
      } finally {
        this.isLoggingIn = false
      }
    },
    async loginWithTelegram(initData, refCode = null) {
      this.isLoggingIn = true
      this.loginError = null
      try {
        const payload = await loginWithTelegramApi({
          initData,
          refCode: refCode ?? useInviteRefStore().inviteRef ?? null,
        })
        if (payload?.data) {
          this.setUser(payload.data)
          this.validateRoleForApp()
        } else if (payload?.user) {
          this.setUser(payload.user)
          this.validateRoleForApp()
        }
        await syncAfterAuth(this.user)
        return payload
      } catch (error) {
        this.loginError = error
        throw error
      } finally {
        this.isLoggingIn = false
      }
    },
    async logout() {
      try {
        await logoutApi()
      } catch (error) {
        console.warn('[auth] logout failed', error)
      } finally {
        teardownRealtime()
        clearAppSessionState()
        this.setUser(null)
        this.roleMismatch = false
        useCartStore().clear()
        useDeliveryAddressStore().clear()
      }
    },
  },
})
